import React, { ChangeEvent, FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { useFormik } from 'formik';

import ExchangeIcon from '../../../assets/icons/exchange-icon';
import DetailsContainer from '../../../containers/details-container';
import AppPageMain from '../../../modules/app-page/app-page--main';
import IconBtn from '../../../modules/icon-btn/icon-btn';
import ModuleInfo from '../../../modules/module-info/module-info';
import PageHeader from '../../../modules/page-header';
import UiInput from '../../../modules/ui/ui-input/ui-input';
import UiSelect, { OptionType } from '../../../modules/ui/ui-select/ui-select';
import { RootState } from '../../../types';
import currencyFormat from '../../../utils/currencyFormat';
import { BankType, OPTIONS } from '../mock';

import './index.scss';

type FormStateType = {
  sale?: string;
  buy?: string;
  saleBank?: BankType;
  buyBank?: BankType;
};

const initialFormState: FormStateType = {
  saleBank: OPTIONS[0].value as BankType,
};
const limit = { min: 10000, max: 100000 };

const ExternalExchange: FC = () => {
  const history = useHistory();
  const ExchangeBanks = useSelector((s: RootState) => s.exchange.exchangeBanks);
  const [loading, setLoading] = useState(false);

  const formState = useFormik({
    initialValues: initialFormState,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: (values) => {
      if (values.sale && values.buy) {
        setLoading(true);
        history.push('/external-exchange-sign', { exchangeInfo: values });
        setLoading(false);
      }
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (
        values.sale &&
        parseFloat(values.sale.replace(/\s/g, '')) < limit.min
      ) {
        errors.sale = `Минимальная сумма ${limit.min}`;
      }
      if (
        values.sale &&
        parseFloat(values.sale.replace(/\s/g, '')) > limit.max
      ) {
        errors.sale = `Максимальная сумма ${limit.max}`;
      }
      if (
        values.sale &&
        values.saleBank &&
        parseFloat(values.sale.replace(/\s/g, '')) >
          ExchangeBanks[values.saleBank].balance
      ) {
        errors.sale = 'Недостаточно средств';
      }
      if (
        values.buy &&
        values.buyBank &&
        parseFloat(values.buy.replace(/\s/g, '')) >
          ExchangeBanks[values.buyBank].balance
      ) {
        errors.buy = 'Недостаточно средств';
      }

      return errors;
    },
  });

  const getFieldError = (field: 'sale' | 'buy') => (
    <div className="form-item__footer">
      <span className={formState.errors[field] && 'red'}>
        {formState.errors[field]
          ? formState.errors[field]
          : field === 'sale'
          ? `Доступно: ${
              formState.values.saleBank &&
              ExchangeBanks[formState.values.saleBank].balance.toLocaleString(
                'ru'
              )
            }`
          : `Доступно: ${
              formState.values.buyBank &&
              ExchangeBanks[formState.values.buyBank].balance.toLocaleString(
                'ru'
              )
            }`}
      </span>
    </div>
  );

  return (
    <AppPageMain>
      <div className="container">
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <PageHeader
              items={[{ link: '/external-exchange', title: 'Обмен' }]}
              iconType="exchange"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35 exchange-container">
            <DetailsContainer
              title="Обмен цифровых финансовых активов"
              onClose={() => history.push('/')}
              infoTitle={{
                steps: [
                  { title: 'Параметры', active: true },
                  { title: 'Подпись' },
                  { title: 'Согласование' },
                  { title: 'Обмен' },
                ],
              }}
              leftComponent={
                <>
                  <form className="flex">
                    <div className="exchange-input-container">
                      <UiInput
                        name="sale"
                        value={formState.values.sale}
                        lg
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          formState.setValues({
                            ...formState.values,
                            sale: e.target.value,
                            buy:
                              formState.values.buyBank &&
                              formState.values.saleBank &&
                              (
                                Number(e.target.value) *
                                ExchangeBanks[formState.values.saleBank].rate[
                                  formState.values.buyBank
                                ]
                              ).toFixed(2),
                          });
                        }}
                        label="Сумма:"
                      />
                      <UiSelect
                        options={OPTIONS.filter(
                          (o) => o.value !== formState.values.buyBank
                        )}
                        lg
                        onChange={(val) => {
                          formState.setValues({
                            ...formState.values,
                            saleBank: (val as OptionType)?.value,
                            buy:
                              formState.values.buyBank &&
                              (
                                Number(formState.values.sale) *
                                ExchangeBanks[
                                  (val as OptionType)?.value as BankType
                                ].rate[formState.values.buyBank]
                              ).toFixed(2),
                          });
                        }}
                        value={OPTIONS[0]}
                      />
                      {getFieldError('sale')}
                    </div>
                    <ExchangeIcon />
                    <div className="exchange-input-container">
                      <UiInput
                        name="buy"
                        value={formState.values.buy}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          formState.setValues({
                            ...formState.values,
                            buy: e.target.value,
                            sale:
                              formState.values.buyBank &&
                              formState.values.saleBank &&
                              (
                                Number(e.target.value) /
                                ExchangeBanks[formState.values.buyBank].rate[
                                  formState.values.saleBank
                                ]
                              ).toFixed(2),
                          });
                        }}
                        label="Сумма:"
                        lg
                      />
                      <UiSelect
                        options={OPTIONS.filter(
                          (o) => o.value !== formState.values.saleBank
                        )}
                        lg
                        onChange={(val) => {
                          formState.setValues({
                            ...formState.values,
                            buyBank: (val as OptionType)?.value,
                            buy:
                              formState.values.saleBank &&
                              (val as OptionType)?.value &&
                              formState.values.sale &&
                              (
                                Number(formState.values.sale) *
                                ExchangeBanks[formState.values.saleBank].rate[
                                  (val as OptionType)?.value as BankType
                                ]
                              ).toFixed(2),
                          });
                        }}
                      />
                      {formState.values.buyBank && getFieldError('buy')}
                    </div>
                  </form>
                  {formState.values.buyBank && formState.values.saleBank && (
                    <div className="flex">
                      <ModuleInfo
                        topPrefix={`1 ${
                          ExchangeBanks[formState.values.saleBank].name
                        } = ${
                          ExchangeBanks[formState.values.saleBank].rate[
                            formState.values.buyBank
                          ]
                        } ${ExchangeBanks[formState.values.buyBank].name}`}
                        bottomDesc="Курс обмена"
                      />
                      {formState.values.sale && (
                        <ModuleInfo
                          topPrefix={`${(
                            Number(formState.values.sale) * 0.2
                          ).toFixed(2)} ${
                            ExchangeBanks[formState.values.saleBank].name
                          }`}
                          bottomDesc="Комиссия"
                        />
                      )}
                    </div>
                  )}

                  <p className="form-title">Цифровые активы в наличии:</p>
                  <div className="available-finance__container">
                    {OPTIONS.map((o) => (
                      <div className="available-finance__item">
                        {o.label}
                        <div className="available-finance__right">
                          {ExchangeBanks[
                            o.value as BankType
                          ].balance.toLocaleString('ru')}
                          <span className="available-finance__info">
                            <span className="gray">
                              {currencyFormat(
                                ExchangeBanks[o.value as BankType].balance *
                                  ExchangeBanks[o.value as BankType].rate.sigma
                              )}{' '}
                            </span>
                            {formState.values.saleBank &&
                              ExchangeBanks[o.value as BankType].rate[
                                formState.values.saleBank
                              ]}
                            <span
                              className={
                                ExchangeBanks[o.value as BankType].change > 0
                                  ? 'green'
                                  : 'red'
                              }
                            >
                              {' '}
                              {ExchangeBanks[o.value as BankType].change}
                            </span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              }
              footer={
                <div className="flex justify-end">
                  {formState.values.buy &&
                    formState.values.saleBank &&
                    formState.values.buyBank &&
                    formState.values.sale && (
                      <div className="module-title-text">
                        Обмен:
                        <span>
                          {Number(formState.values.sale).toLocaleString('ru')}
                        </span>
                        {ExchangeBanks[formState.values.saleBank].name} на
                        <span>
                          {Number(formState.values.buy).toLocaleString('ru')}
                        </span>
                        {ExchangeBanks[formState.values.buyBank].name}
                      </div>
                    )}
                  <IconBtn
                    onClick={formState.handleSubmit}
                    lg
                    disabled={
                      loading ||
                      !formState.isValid ||
                      !formState.values.buyBank ||
                      !formState.values.buy ||
                      !formState.values.saleBank ||
                      !formState.values.sale
                    }
                    icon={<ExchangeIcon color="white" />}
                    title="Обменять"
                  />
                </div>
              }
            />
          </div>
        </div>
      </div>
    </AppPageMain>
  );
};

export default ExternalExchange;
